import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import LandingRoute from "@/components/LandingRoute";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireDeskAccess } from "@/components/RequireDeskAccess";
import { RequireSuperAdmin } from "@/components/RequireSuperAdmin";
import { AuthProvider } from "@/lib/AuthContext";
import { DemoIdentityProvider } from "@/lib/DemoIdentityContext";
import { TenantConfigProvider } from "@/lib/TenantConfigContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Overview from "@/pages/Overview";
import JourneyList from "@/pages/JourneyList";
import JourneyDetail from "@/pages/JourneyDetail";
import CreateGroup from "@/pages/CreateGroup";
import { OrgRegions, OrgDealers, OrgWorkshops, OrgPeople, OrgRoles } from "@/pages/Organization";
import IntakeQR from "@/pages/IntakeQR";
import Escalations from "@/pages/Escalations";
import CustomerApprovalPending from "@/pages/CustomerApprovalPending";
import PendingResponse from "@/pages/PendingResponse";
import PreIfr from "@packs/automotive/web/pages/PreIfr";
import MsdWorkshopAnalytics from "@packs/automotive/web/pages/msd/WorkshopAnalytics";
import MsdServiceAnalytics from "@/pages/msd/ServiceAnalytics";
import { MsdPage, MsdShell } from "@/components/msd/Shell";
import EscalationDetail from "@/pages/EscalationDetail";
import Notifications from "@/pages/Notifications";
import ChatHistorySearch from "@/pages/ChatHistorySearch";
import ServiceDesk from "@/pages/ServiceDesk";
import SuperAdmin from "@/pages/SuperAdmin";
import SystemHealthPage from "@/pages/SystemHealthPage";
import GroupFiles from "@/pages/GroupFiles";

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* The page a customer opens from the link in their WhatsApp group.
              Public on purpose: the token in the link is the only credential. */}
          <Route path="/files/:token" element={<GroupFiles />} />
          {/* Service Desk: three panes inside the shared app shell. Replaces the
              Service Journey tab once that retires. */}
          <Route
            path="/desk"
            element={
              <RequireAuth>
                <RequireDeskAccess>
                  <DemoIdentityProvider>
                    <TenantConfigProvider>
                      <MsdShell>
                        <ServiceDesk />
                      </MsdShell>
                    </TenantConfigProvider>
                  </DemoIdentityProvider>
                </RequireDeskAccess>
              </RequireAuth>
            }
          />
          {/* Every brand-side page sits in the Maruti Service Desk shell. */}
          <Route
            path="/analytics"
            element={
              <RequireAuth>
                <DemoIdentityProvider>
                  <TenantConfigProvider>
                    <MsdShell>
                      <MsdWorkshopAnalytics />
                    </MsdShell>
                  </TenantConfigProvider>
                </DemoIdentityProvider>
              </RequireAuth>
            }
          />
          <Route
            path="/service-analytics"
            element={
              <RequireAuth>
                <DemoIdentityProvider>
                  <TenantConfigProvider>
                    <MsdShell>
                      <MsdServiceAnalytics />
                    </MsdShell>
                  </TenantConfigProvider>
                </DemoIdentityProvider>
              </RequireAuth>
            }
          />
          {/* Platform-side, not brand-side: configures the terminology every
              other route reads, so it sits outside the dealer chrome. */}
          <Route
            path="/superadmin/*"
            element={
              <RequireAuth>
                <RequireSuperAdmin>
                  <SuperAdmin />
                </RequireSuperAdmin>
              </RequireAuth>
            }
          />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <DemoIdentityProvider>
                  <TenantConfigProvider>
                    <MsdShell>
                      <MsdPage>
                      <Routes>
                        <Route path="/" element={<LandingRoute />} />
                        <Route path="/overview" element={<Overview />} />
                        <Route path="/journey" element={<JourneyList />} />
                        <Route path="/journey/:row" element={<JourneyDetail />} />
                        <Route path="/chat-history" element={<ChatHistorySearch />} />
                        <Route path="/create" element={<CreateGroup />} />
                        {/* Matches the QR-code URL exactly (see docs/qr-intake.md) — same component as /create */}
                        <Route path="/create-group" element={<CreateGroup />} />
                        <Route path="/intake-qr" element={<IntakeQR />} />
                        {/* Workshop and Workshops were one thing all along. */}
                        <Route path="/stakeholders" element={<Navigate to="/workshops" replace />} />
                        {/* Structure was the drill-down; the flat pages replaced it. */}
                        <Route path="/structure" element={<Navigate to="/regions" replace />} />
                        <Route path="/regions" element={<OrgRegions />} />
                        <Route path="/dealers" element={<OrgDealers />} />
                        <Route path="/workshops" element={<OrgWorkshops />} />
                        <Route path="/people" element={<OrgPeople />} />
                        <Route path="/roles" element={<OrgRoles />} />
                        <Route path="/escalations" element={<Escalations />} />
                        <Route path="/customer-approval-pending" element={<CustomerApprovalPending />} />
                        <Route path="/pending-response" element={<PendingResponse />} />
                        <Route path="/pre-ifr" element={<PreIfr />} />
                        <Route path="/analytics/escalations" element={<EscalationDetail />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/system-health" element={<SystemHealthPage />} />
                      </Routes>
                      </MsdPage>
                    </MsdShell>
                  </TenantConfigProvider>
                </DemoIdentityProvider>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
